import { useMatchesData } from "./useMatches.js";

export function useOptionalUser() {
  const data = useMatchesData("root", false);
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser() {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    // During client-side navigation, the root loader may return no user
    // (e.g. session expired, CDN-cached page) while components from the
    // previous route are still mounted. Redirect instead of crashing.
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new Error(
      "No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.",
    );
  }
  return maybeUser;
}

function isUser(user) {
  return user && typeof user === "object" && typeof user.email === "string";
}

export function useUserSubscriptionValue({
  trial = null,
  active = null,
  active_unlimited = active,
  active_flexible = active,
  active_basic = active,
  active_pro = active,
  active_student = active,
  none = null,
}) {
  const user = useOptionalUser();

  if (!user) {
    return typeof none === "function" ? none() : none;
  }

  const statuses = {
    active_unlimited,
    active_flexible,
    active_basic,
    active_pro,
    active_student,
    trial,
  };

  const status = statuses[user.subscription.status];

  if (typeof status !== "undefined") {
    return typeof status === "function" ? status(user) : status;
  }

  throw new Error(
    `Unhandled subscription "${user.subscription.status}" for ${user.email} (id: ${user.id})`,
  );
}

export function useUserAvailabilityRequestLimit() {
  const user = useOptionalUser();

  const canSubscribe = useUserSubscriptionValue({
    none: () => false,
    trial: (user) => getEffectiveMaxActiveRequests(user.subscription) !== 7, // I don't remember why we have this hardcoded here. Why would a trial user have a limit of 7?
    active: () => false,
    active_basic: () => true,
    active_student: () => true,
  });

  const limit = user
    ? getEffectiveMaxActiveRequests(user.subscription)
    : undefined;

  return {
    limit,
    canSubscribe,
  };
}

function getEffectiveMaxActiveRequests(subscription) {
  return (
    subscription.maxActiveRequestsOverride ?? subscription.maxActiveRequests
  );
}
